import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { LogIn, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';

export const Login = () => {
  const { user, loading: authLoading, login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [loading, setLoading] = useState(false);

  // Logo com fundo branco para tema claro e logo clara para tema escuro
  const logoUrl = theme === 'light'
    ? 'https://customer-assets.emergentagent.com/job_stockwise-app-17/artifacts/wbwkqvrv_Claro.png'
    : 'https://customer-assets.emergentagent.com/job_stockwise-app-17/artifacts/waabplrj_Claro.png';

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    // BUG FIX: só admin pode entrar na área admin. Vendedor/Cliente é redirecionado pro vendor portal.
    if (user.role === 'admin') {
      return <Navigate to="/dashboardadminromaneio/dashboard" replace />;
    }
    if (user.role === 'vendedor') {
      return <Navigate to="/vendedor/dashboard" replace />;
    }
    if (user.role === 'cliente') {
      return <Navigate to="/login" replace />;
    }
    // Role desconhecida: força logout (proteção)
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const opts = needsTotp ? { totp_code: totpCode } : {};
    const result = await login(email, password, opts);

    if (result.success) {
      toast.success('Login realizado com sucesso!');
      setNeedsTotp(false);
      setTotpCode('');
    } else if (result.totpRequired) {
      setNeedsTotp(true);
      toast.info('Digite o código do Google Authenticator');
    } else {
      if (needsTotp) setTotpCode('');
      toast.error(result.message || 'Erro ao fazer login');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary via-secondary to-primary/20" />
      
      {/* Animated shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        data-testid="theme-toggle-login"
        className="absolute top-6 right-6 z-50 p-3 rounded-lg bg-white/10 backdrop-blur-md hover:bg-white/20 transition-all hover:scale-105 active:scale-95 shadow-lg"
        title={theme === 'light' ? 'Modo Escuro' : 'Modo Claro'}
      >
        {theme === 'light' ? (
          <Moon className="w-5 h-5 text-white" />
        ) : (
          <Sun className="w-5 h-5 text-primary" />
        )}
      </button>

      {/* Login Card */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div
            className="bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border border-white/20 dark:border-white/10 shadow-2xl rounded-2xl p-8"
            data-testid="login-card"
          >
            {/* Logo */}
            <div className="text-center mb-8">
              <img
                src={logoUrl}
                alt="Distribuidora Santa Ana"
                className="h-32 w-auto mx-auto mb-4 object-contain"
              />
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Bem-vindo
              </h1>
              <p className="text-muted-foreground">
                Sistema de Gestão Empresarial
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200"
                  placeholder="seu@email.com"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Senha
                </label>
                <input
                  id="password"
                  type="password"
                  data-testid="password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="flex h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200"
                  placeholder="••••••••"
                />
              </div>

              {needsTotp && (
                <div className="space-y-2" data-testid="totp-section">
                  <label className="block text-sm font-medium text-foreground">
                    Código do Google Authenticator
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    required
                    autoFocus
                    placeholder="123456"
                    className="flex h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-center text-lg tracking-widest font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    data-testid="totp-input"
                  />
                  <p className="text-xs text-muted-foreground">Abra o app Google Authenticator e digite os 6 dígitos.</p>
                </div>
              )}

              <button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-orange-500/20 transition-all hover:scale-[1.02] active:scale-[0.98] font-semibold rounded-lg px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    Entrar
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-6 text-center text-sm text-muted-foreground">
              <p>Sistema exclusivo para uso interno</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
